import maplibregl from 'maplibre-gl';
import mlcontour from '@acalcutt/maplibre-contour-pmtiles';
import { store, AppState } from '../state';
import { getApiServer, buildPmtilesUrl } from '../config';

export class ContourService {
  private static instance: ContourService;
  private demSource: any = null;
  private currentUnits: string | null = null;
  private isProtocolSetup = false;
  private map: maplibregl.Map | null = null;

  public static getInstance(): ContourService {
    if (!ContourService.instance) {
      ContourService.instance = new ContourService();
    }
    return ContourService.instance;
  }

  public init(map: maplibregl.Map) {
    this.map = map;
    this.setupDemSource();
  }

  private setupDemSource() {
    if (this.demSource) return;

    const manifest = store.getState().manifest;
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest ? manifest.tile_base : 'tiles').replace(/^\/+|\/+$/g, '');
    const basemapPath = `${tileRoot}/basemaps`;

    // Construct the canonical PMTiles DEM template URL for maplibre-contour-pmtiles
    const rawDemUrl = `${apiServer}/${basemapPath}/Elevation_merged_0_10.pmtiles`;
    const cleanDemUrl = buildPmtilesUrl(rawDemUrl);

    try {
      this.demSource = new mlcontour.DemSource({
        url: cleanDemUrl,
        encoding: 'terrarium',
        maxzoom: 10,
        worker: true,
        cacheSize: 100
      });

      if (!this.isProtocolSetup) {
        this.demSource.setupMaplibre(maplibregl);
        this.isProtocolSetup = true;
      }
    } catch (err) {
      console.warn('ContourService DemSource initialization error:', err);
    }
  }

  public updateContours(state: AppState) {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const isEnabled = state.elevationContoursEnabled === true;
    const theme = state.theme;
    // Exclude USGS Topo (already has baked-in USGS contours)
    const isExcludedTheme = theme === 'usgs-topo';

    const sourceId = 'elevation-contours-source';
    const minorLayerId = 'elevation-contours-minor';
    const majorLayerId = 'elevation-contours-major';
    const labelLayerId = 'elevation-contours-labels';

    if (!isEnabled || isExcludedTheme) {
      if (this.map.getLayer(labelLayerId)) this.map.setLayoutProperty(labelLayerId, 'visibility', 'none');
      if (this.map.getLayer(majorLayerId)) this.map.setLayoutProperty(majorLayerId, 'visibility', 'none');
      if (this.map.getLayer(minorLayerId)) this.map.setLayoutProperty(minorLayerId, 'visibility', 'none');
      return;
    }

    this.setupDemSource();
    if (!this.demSource) return;

    const isImperial = state.units !== 'metric';
    const multiplier = isImperial ? 3.28084 : 1.0;
    const unitsKey = isImperial ? 'imperial' : 'metric';

    // If units changed, we need to rebuild the source with updated interval multipliers
    if (this.currentUnits !== unitsKey && this.map.getSource(sourceId)) {
      if (this.map.getLayer(labelLayerId)) this.map.removeLayer(labelLayerId);
      if (this.map.getLayer(majorLayerId)) this.map.removeLayer(majorLayerId);
      if (this.map.getLayer(minorLayerId)) this.map.removeLayer(minorLayerId);
      this.map.removeSource(sourceId);
    }

    if (!this.map.getSource(sourceId)) {
      this.currentUnits = unitsKey;

      const thresholds = isImperial ? {
        9: [500, 2500],
        10: [200, 1000],
        11: [100, 500],
        12: [50, 250],
        13: [40, 200],
        14: [20, 100]
      } : {
        9: [200, 1000],
        10: [100, 500],
        11: [50, 250],
        12: [25, 100],
        13: [20, 100],
        14: [10, 50]
      };

      const contourUrl = this.demSource.contourProtocolUrl({
        multiplier,
        overzoom: 1,
        thresholds,
        elevationKey: 'ele',
        levelKey: 'level',
        contourLayer: 'contours'
      });

      this.map.addSource(sourceId, {
        type: 'vector',
        tiles: [contourUrl],
        maxzoom: 15
      });
    }

    const colorMode = state.vectorThemeMode || (theme === 'light' ? 'light' : 'dark');
    const minorColor = colorMode === 'light' ? 'rgba(146, 64, 14, 0.40)' : 'rgba(245, 158, 11, 0.35)';
    const majorColor = colorMode === 'light' ? 'rgba(120, 53, 15, 0.75)' : 'rgba(251, 191, 36, 0.70)';
    const labelColor = colorMode === 'light' ? '#78350f' : '#fef3c7';
    const labelHalo = colorMode === 'light' ? 'rgba(255, 255, 255, 0.90)' : 'rgba(15, 23, 42, 0.90)';

    // Find layer to insert before (above terrain/basemap, below symbols/trails/weather)
    const layers = this.map.getStyle().layers || [];
    let beforeId: string | undefined = undefined;
    const firstSymbol = layers.find(l => 
      l.type === 'symbol' || 
      l.id.startsWith('hiking-trails') || 
      l.id.startsWith('major-trails') ||
      l.id.startsWith('pmtiles-') ||
      l.id.startsWith('radar')
    );
    if (firstSymbol) beforeId = firstSymbol.id;

    // 1. Minor Contours Layer
    if (!this.map.getLayer(minorLayerId)) {
      this.map.addLayer({
        id: minorLayerId,
        type: 'line',
        source: sourceId,
        'source-layer': 'contours',
        filter: ['==', ['get', 'level'], 0],
        paint: {
          'line-color': minorColor,
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            9, 0.4,
            11, 0.6,
            13, 0.8,
            15, 1.1
          ]
        }
      }, beforeId);
    } else {
      this.map.setLayoutProperty(minorLayerId, 'visibility', 'visible');
      this.map.setPaintProperty(minorLayerId, 'line-color', minorColor);
    }

    // 2. Major Index Contours Layer
    if (!this.map.getLayer(majorLayerId)) {
      this.map.addLayer({
        id: majorLayerId,
        type: 'line',
        source: sourceId,
        'source-layer': 'contours',
        filter: ['>', ['get', 'level'], 0],
        paint: {
          'line-color': majorColor,
          'line-width': [
            'interpolate', ['linear'], ['zoom'],
            9, 0.8,
            11, 1.1,
            13, 1.5,
            15, 2.0
          ]
        }
      }, beforeId);
    } else {
      this.map.setLayoutProperty(majorLayerId, 'visibility', 'visible');
      this.map.setPaintProperty(majorLayerId, 'line-color', majorColor);
    }

    // 3. Elevation Contour Labels Layer
    if (!this.map.getLayer(labelLayerId)) {
      const unitSuffix = isImperial ? ' ft' : ' m';
      this.map.addLayer({
        id: labelLayerId,
        type: 'symbol',
        source: sourceId,
        'source-layer': 'contours',
        minzoom: 11,
        filter: ['>', ['get', 'level'], 0],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['concat', ['to-string', ['get', 'ele']], unitSuffix],
          'text-font': ['Noto Sans Regular'],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            11, 9,
            13, 10,
            15, 11
          ],
          'text-letter-spacing': 0.05,
          'text-max-angle': 35,
          'symbol-spacing': 350
        },
        paint: {
          'text-color': labelColor,
          'text-halo-color': labelHalo,
          'text-halo-width': 1.8
        }
      });
    } else {
      this.map.setLayoutProperty(labelLayerId, 'visibility', 'visible');
      this.map.setPaintProperty(labelLayerId, 'text-color', labelColor);
      this.map.setPaintProperty(labelLayerId, 'text-halo-color', labelHalo);
    }
  }
}
